import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";

interface BookingData {
  excursionSlug: string;
  excursionTitle: string;
  variantLabel: string;
  variantPrice: number;
  adults: number;
  children: number;
  infants: number;
  total: number;
  date: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  data: BookingData;
}

export default function BookingFormModal({ open, onClose, data }: Props) {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotField = form.elements.namedItem("phone_alt") as HTMLInputElement;
    if (honeypotField && honeypotField.value.trim() !== "") {
      setStatus("success");
      setErrorMsg("");
      return;
    }

    setStatus("sending");
    setErrorMsg("");

    try {
      const formData = new FormData(form);

      // Send booking email to owner via Edge Function
      const { error: fnError } = await supabase.functions.invoke('send-booking-email', {
        body: {
          name: String(formData.get("name") || ""),
          email: String(formData.get("email") || ""),
          phone: String(formData.get("phone") || ""),
          message: String(formData.get("message") || "") || null,
          excursionTitle: data.excursionTitle,
          variantLabel: data.variantLabel,
          variantPrice: data.variantPrice,
          adults: data.adults,
          children: data.children,
          infants: data.infants,
          total: data.total,
          date: data.date || null,
        },
      });

      if (fnError) throw fnError;

      setStatus("success");
      form.reset();
      setTimeout(() => {
        onClose();
        setTimeout(() => setStatus("idle"), 300);
      }, 2500);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("ct_form_error");
      setErrorMsg(msg);
      setStatus("error");
    }
  }

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background-50 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="font-heading text-xl font-semibold text-foreground-950">
                {t("bfm_title", "Complete your booking")}
              </h2>
              <p className="text-sm text-foreground-500 mt-1">
                {data.excursionTitle}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 cursor-pointer text-foreground-500"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>

          {status === "success" ? (
            <div className="text-center py-10">
              <div className="w-16 h-16 mx-auto mb-4 bg-accent-100 rounded-full flex items-center justify-center">
                <i className="ri-check-line text-3xl text-accent-600"></i>
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">
                {t("bfm_success_title", "Booking request sent!")}
              </h3>
              <p className="text-sm text-foreground-600">
                {t("bfm_success_body", "We'll confirm your reservation within a few hours via email or WhatsApp.")}
              </p>
            </div>
          ) : (
            <form ref={formRef} onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Booking summary */}
                <div className="bg-background-100 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-foreground-500">{t("bfm_excursion", "Excursion")}</span>
                    <span className="text-foreground-950 font-medium">{data.excursionTitle}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-500">{t("bw_option")}</span>
                    <span className="text-foreground-950 font-medium">{data.variantLabel} &mdash; €{data.variantPrice}</span>
                  </div>
                  {data.date && (
                    <div className="flex justify-between">
                      <span className="text-foreground-500">{t("bfm_date", "Date")}</span>
                      <span className="text-foreground-950 font-medium">{data.date}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-foreground-500">{t("bfm_guests", "Guests")}</span>
                    <span className="text-foreground-950 font-medium">
                      {data.adults} {t("bw_adults").toLowerCase()}
                      {data.children > 0 && `, ${data.children} ${t("bw_children").toLowerCase()}`}
                      {data.infants > 0 && `, ${data.infants} ${t("bw_infants").toLowerCase()}`}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-background-200/70 pt-2 mt-2">
                    <span className="text-foreground-500">{t("bw_total")}</span>
                    <span className="text-foreground-950 font-semibold">€{data.total.toFixed(2)}</span>
                  </div>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                    {t("bfm_name", "Full name")} *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder={t("bfm_name_placeholder", "Your full name")}
                    className="w-full text-sm bg-background-100 border border-background-200 rounded-lg px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                    {t("bfm_email", "Email")} *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder={t("bfm_email_placeholder", "your@email.com")}
                    className="w-full text-sm bg-background-100 border border-background-200 rounded-lg px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                    {t("bfm_phone", "Phone / WhatsApp")} *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder="+48 123 456 789"
                    className="w-full text-sm bg-background-100 border border-background-200 rounded-lg px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                    {t("bfm_message", "Special requests")}
                  </label>
                  <textarea
                    name="message"
                    rows={3}
                    maxLength={500}
                    placeholder={t("bfm_message_placeholder", "Hotel name, pickup location, dietary needs...")}
                    className="w-full text-sm bg-background-100 border border-background-200 rounded-lg px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                  />
                  <p className="text-xs text-foreground-400 mt-1">Max 500 characters</p>
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="phone-alt-field"
                />

                {status === "error" && errorMsg && (
                  <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm flex items-start gap-2">
                    <i className="ri-error-warning-line mt-0.5"></i>
                    <span>{errorMsg}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="w-full bg-accent-500 hover:bg-accent-600 disabled:bg-accent-400 text-background-50 dark:text-foreground-950 py-3 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                >
                  {status === "sending" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      {t("bfm_sending", "Sending...")}
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      {t("bfm_submit", "Send booking request")}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}